import { supabase } from '../supabase/client';

export interface QuestionStat {
  questionId: string;
  paperId: string;
  totalAttempts: number;
  correctAttempts: number;
  wrongAttempts: number;
  correctRate: number;
  autoDifficulty: 'easy' | 'medium' | 'hard';
  lastCalculatedAt: string;
}

export interface UserQuestionRecord {
  questionId: string;
  paperId: string;
  isCorrect: boolean;
  answer: string;
  answeredAt: string;
}

export async function getQuestionStats(): Promise<Record<string, QuestionStat>> {
  const { data, error } = await supabase.from('question_stats').select('*');
  if (error) {
    
    return {};
  }
  const stats: Record<string, QuestionStat> = {};
  data?.forEach((row: any) => {
    const key = `${row.paper_id}_${row.question_id}`;
    stats[key] = {
      questionId: row.question_id,
      paperId: row.paper_id,
      totalAttempts: row.total_attempts || 0,
      correctAttempts: row.correct_attempts || 0,
      wrongAttempts: row.wrong_attempts || 0,
      correctRate: parseFloat(row.correct_rate) || 0,
      autoDifficulty: row.auto_difficulty || 'medium',
      lastCalculatedAt: row.last_calculated_at,
    };
  });
  return stats;
}

export async function getQuestionStat(questionId: string, paperId: string): Promise<QuestionStat | null> {
  const { data, error } = await supabase
    .from('question_stats')
    .select('*')
    .eq('question_id', questionId)
    .eq('paper_id', paperId)
    .maybeSingle();
  if (error || !data) return null;
  return {
    questionId: data.question_id,
    paperId: data.paper_id,
    totalAttempts: data.total_attempts || 0,
    correctAttempts: data.correct_attempts || 0,
    wrongAttempts: data.wrong_attempts || 0,
    correctRate: parseFloat(String(data.correct_rate)) || 0,
    autoDifficulty: (data.auto_difficulty as any) || 'medium',
    lastCalculatedAt: data.last_calculated_at,
  };
}

export function calculateDifficulty(correctRate: number): 'easy' | 'medium' | 'hard' {
  if (correctRate >= 70) return 'easy';
  if (correctRate >= 30) return 'medium';
  return 'hard';
}

export async function updateQuestionStat(
  questionId: string,
  paperId: string,
  isCorrect: boolean
): Promise<QuestionStat | null> {
  const existing = await getQuestionStat(questionId, paperId);
  const totalAttempts = (existing?.totalAttempts || 0) + 1;
  const correctAttempts = (existing?.correctAttempts || 0) + (isCorrect ? 1 : 0);
  const wrongAttempts = (existing?.wrongAttempts || 0) + (isCorrect ? 0 : 1);
  const correctRate = totalAttempts > 0 ? Math.round((correctAttempts / totalAttempts) * 100) : 0;
  const autoDifficulty = calculateDifficulty(correctRate);
  const lastCalculatedAt = new Date().toISOString();

  const { error } = await supabase.from('question_stats').upsert({
    question_id: questionId,
    paper_id: paperId,
    total_attempts: totalAttempts,
    correct_attempts: correctAttempts,
    wrong_attempts: wrongAttempts,
    correct_rate: correctRate,
    auto_difficulty: autoDifficulty,
    last_calculated_at: lastCalculatedAt,
  }, { onConflict: 'question_id,paper_id' });

  if (error) {
    
    return null;
  }

  return {
    questionId,
    paperId,
    totalAttempts,
    correctAttempts,
    wrongAttempts,
    correctRate,
    autoDifficulty,
    lastCalculatedAt,
  };
}

export async function getQuestionsDifficulty(
  questionIds: string[],
  paperId: string
): Promise<Record<string, 'easy' | 'medium' | 'hard'>> {
  const { data, error } = await supabase
    .from('question_stats')
    .select('question_id, auto_difficulty')
    .eq('paper_id', paperId)
    .in('question_id', questionIds);
  if (error) return {};
  const result: Record<string, 'easy' | 'medium' | 'hard'> = {};
  data?.forEach((row: any) => {
    result[row.question_id] = row.auto_difficulty || 'medium';
  });
  return result;
}

export async function getUserQuestionRecords(userId: string): Promise<UserQuestionRecord[]> {
  const { data, error } = await supabase
    .from('user_question_records')
    .select('*')
    .eq('user_id', userId);
  if (error) {
    
    return [];
  }
  return data?.map((row: any) => ({
    questionId: row.question_id,
    paperId: row.paper_id,
    isCorrect: row.is_correct,
    answer: row.answer || '',
    answeredAt: row.answered_at,
  })) || [];
}

export async function saveUserQuestionRecord(
  userId: string,
  record: UserQuestionRecord
): Promise<boolean> {
  const { error } = await supabase.from('user_question_records').upsert({
    user_id: userId,
    question_id: record.questionId,
    paper_id: record.paperId,
    is_correct: record.isCorrect,
    answer: record.answer,
    answered_at: record.answeredAt,
  }, { onConflict: 'user_id,question_id,paper_id' });
  if (error) {
    
    return false;
  }
  return true;
}

export async function getUserWrongRecords(userId: string): Promise<UserQuestionRecord[]> {
  const records = await getUserQuestionRecords(userId);
  return records.filter(r => !r.isCorrect);
}

export async function isQuestionWrong(
  userId: string,
  questionId: string,
  paperId: string
): Promise<boolean> {
  const { data, error } = await supabase
    .from('user_question_records')
    .select('is_correct')
    .eq('user_id', userId)
    .eq('question_id', questionId)
    .eq('paper_id', paperId)
    .maybeSingle();
  if (error || !data) return false;
  return !data.is_correct;
}

export async function getUserWrongQuestionIds(userId: string): Promise<Set<string>> {
  const wrongRecords = await getUserWrongRecords(userId);
  return new Set(wrongRecords.map(r => r.questionId));
}

export async function countUserWrongQuestions(userId: string): Promise<number> {
  const { count, error } = await supabase
    .from('user_question_records')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('is_correct', false);
  if (error) return 0;
  return count || 0;
}
